type SkeletonVariant = 'text' | 'card' | 'table-row' | 'circle' | 'rect';

interface LoadingSkeletonProps {
  variant?: SkeletonVariant;
  /** Number of skeleton items to render */
  count?: number;
  className?: string;
  /** Width override (e.g. "80%", "200px") */
  width?: string;
  /** Height override */
  height?: string;
}

function SkeletonPulse({
  className = '',
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={`animate-pulse rounded-[var(--radius-md)] bg-[var(--bg-hover)] ${className}`}
      style={style}
      aria-hidden="true"
    />
  );
}

function TextSkeleton({ width }: { width?: string }) {
  return (
    <div className="space-y-2.5">
      <SkeletonPulse className="h-4" style={{ width: width ?? '100%' }} />
      <SkeletonPulse className="h-4" style={{ width: '85%' }} />
      <SkeletonPulse className="h-4" style={{ width: '70%' }} />
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="rounded-[var(--radius-lg)] border border-[var(--glass-border)] bg-[var(--glass-bg)] p-4 space-y-3">
      <div className="flex items-center gap-3">
        <SkeletonPulse className="w-10 h-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <SkeletonPulse className="h-4 w-3/4" />
          <SkeletonPulse className="h-3 w-1/2" />
        </div>
      </div>
      <SkeletonPulse className="h-4 w-full" />
      <SkeletonPulse className="h-4 w-5/6" />
      <div className="flex gap-2 pt-1">
        <SkeletonPulse className="h-6 w-16 rounded-full" />
        <SkeletonPulse className="h-6 w-20 rounded-full" />
      </div>
    </div>
  );
}

function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-[var(--glass-border)] last:border-b-0">
      <SkeletonPulse className="h-4 w-24 shrink-0" />
      <SkeletonPulse className="h-4 flex-1" />
      <SkeletonPulse className="h-4 w-20 shrink-0" />
      <SkeletonPulse className="h-6 w-16 rounded-full shrink-0" />
    </div>
  );
}

export function LoadingSkeleton({
  variant = 'text',
  count = 1,
  className = '',
  width,
  height,
}: LoadingSkeletonProps) {
  const items = Array.from({ length: count }, (_, i) => i);

  if (variant === 'circle' || variant === 'rect') {
    return (
      <div className={`flex flex-wrap gap-3 ${className}`}>
        {items.map((i) => (
          <SkeletonPulse
            key={i}
            className={variant === 'circle' ? 'rounded-full' : ''}
            style={{
              width: width ?? (variant === 'circle' ? '40px' : '100%'),
              height: height ?? (variant === 'circle' ? '40px' : '24px'),
            }}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`} role="status" aria-label="Loading">
      <span className="sr-only">Loading...</span>
      {variant === 'text' && items.map((i) => <TextSkeleton key={i} width={width} />)}
      {variant === 'card' && items.map((i) => <CardSkeleton key={i} />)}
      {variant === 'table-row' && (
        <div className="rounded-[var(--radius-lg)] border border-[var(--glass-border)] bg-[var(--glass-bg)] overflow-hidden">
          {items.map((i) => (
            <TableRowSkeleton key={i} />
          ))}
        </div>
      )}
    </div>
  );
}
